import React from 'react';
import { X, Wallet, TrendingUp, Star, ArrowDownCircle, ArrowUpCircle, Clock } from 'lucide-react';

const Field = ({ label, value }) => (
  <div>
    <p className="text-[10px] font-bold text-gray-600 uppercase tracking-widest mb-0.5">{label}</p>
    <p className="text-sm text-gray-200 break-all">{value || 'N/A'}</p>
  </div>
);

const DepositStatusPill = ({ status = '' }) => {
  const s = status.toLowerCase();
  if (s === 'paid')     return <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-green-500/10 border border-green-500/20 text-green-400">Paid</span>;
  if (s === 'rejected') return <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-500/10 border border-red-500/20 text-red-400">Rejected</span>;
  return <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-500/10 border border-amber-500/20 text-amber-400">Pending</span>;
};

const WithdrawStatusPill = ({ status = '' }) => {
  if (status === 'Completed') return <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-green-500/10 border border-green-500/20 text-green-400">Completed</span>;
  if (status === 'Rejected')  return <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-500/10 border border-red-500/20 text-red-400">Rejected</span>;
  return <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-500/10 border border-amber-500/20 text-amber-400">Pending</span>;
};

const UserDetailsModal = ({ onClose, loading, userDetails }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
    <div className="bg-[#0d1526] border border-white/10 rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
      <div className="sticky top-0 bg-[#0d1526] border-b border-white/8 flex items-center justify-between px-6 py-4 z-10">
        <h2 className="text-base font-bold text-white">User Details</h2>
        <button onClick={onClose} className="p-1.5 rounded-lg text-gray-500 hover:text-white hover:bg-white/8 transition-all">
          <X size={18} />
        </button>
      </div>

      <div className="p-6">
        {loading ? (
          <div className="space-y-3 py-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-8 rounded-lg bg-white/5 animate-pulse" />
            ))}
          </div>
        ) : userDetails ? (
          <div className="space-y-6">
            {/* Profile header */}
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-cyan-500 to-purple-500 flex items-center justify-center text-white text-xl font-bold shrink-0 overflow-hidden">
                {userDetails.profilePictureUrl
                  ? <img src={userDetails.profilePictureUrl} alt="Profile" className="w-full h-full object-cover" />
                  : (userDetails.name || 'U').charAt(0).toUpperCase()
                }
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">{userDetails.name}</h3>
                <p className="text-sm text-gray-500">{userDetails.email}</p>
                <span className="inline-block mt-1 px-2 py-0.5 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 text-[10px] font-bold uppercase tracking-wider">
                  {userDetails.role || 'user'}
                </span>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-3">
              <div className="flex items-center gap-3 p-3 rounded-xl bg-green-500/8 border border-green-500/15">
                <Wallet size={18} className="text-green-400 shrink-0" />
                <div>
                  <p className="text-[10px] text-gray-500">Balance</p>
                  <p className="text-base font-bold text-green-400">${userDetails.totalBalance?.toFixed(2)}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 rounded-xl bg-blue-500/8 border border-blue-500/15">
                <TrendingUp size={18} className="text-blue-400 shrink-0" />
                <div>
                  <p className="text-[10px] text-gray-500">Profit</p>
                  <p className="text-base font-bold text-blue-400">${userDetails.totalProfit?.toFixed(2)}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 rounded-xl bg-yellow-500/8 border border-yellow-500/15">
                <Star size={18} className="text-yellow-400 shrink-0" />
                <div>
                  <p className="text-[10px] text-gray-500">Ratings</p>
                  <p className="text-base font-bold text-yellow-400">{userDetails.ratingCount}</p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Left: account info */}
              <div className="space-y-4">
                <h4 className="text-xs font-bold text-gray-600 uppercase tracking-widest border-b border-white/5 pb-2">Account</h4>
                <Field label="Username" value={userDetails.username} />
                <Field label="Mobile" value={userDetails.mobile} />
                <Field label="Country" value={userDetails.country} />
                <Field label="Registered" value={userDetails.createdAt ? new Date(userDetails.createdAt).toLocaleString() : null} />
                {userDetails.lastActivity && (
                  <div>
                    <p className="text-[10px] font-bold text-gray-600 uppercase tracking-widest mb-0.5 flex items-center gap-1">
                      <Clock size={10} /> Last Active
                    </p>
                    <p className="text-sm text-gray-200">{new Date(userDetails.lastActivity).toLocaleString()}</p>
                  </div>
                )}
                <Field label="Invite Code" value={userDetails.invitationCode} />

                <h4 className="text-xs font-bold text-gray-600 uppercase tracking-widest border-b border-white/5 pb-2 pt-2">Withdrawal</h4>
                <Field label="Network" value={userDetails.withdrawNetwork} />
                <Field label="Address" value={userDetails.withdrawAddress} />
                <Field label="Exchange" value={userDetails.withdrawExchange} />
              </div>

              {/* Right: history */}
              <div className="space-y-4">
                <div>
                  <h4 className="text-xs font-bold text-gray-600 uppercase tracking-widest border-b border-white/5 pb-2 mb-3 flex items-center gap-1.5">
                    <ArrowDownCircle size={13} className="text-green-400" /> Deposit History
                  </h4>
                  <ul className="max-h-48 overflow-y-auto space-y-1.5 pr-1">
                    {userDetails.deposits?.length > 0 ? userDetails.deposits.map(d => (
                      <li key={d._id} className="flex justify-between items-center bg-white/3 border border-white/5 rounded-lg px-3 py-1.5 text-xs gap-2">
                        <span className="font-mono text-green-400 font-semibold shrink-0">+${d.amount?.toFixed(2)}</span>
                        <DepositStatusPill status={d.status} />
                        <span className="text-gray-600 shrink-0">{new Date(d.createdAt).toLocaleDateString()}</span>
                      </li>
                    )) : <li className="text-gray-600 text-xs">No deposits</li>}
                  </ul>
                </div>

                <div>
                  <h4 className="text-xs font-bold text-gray-600 uppercase tracking-widest border-b border-white/5 pb-2 mb-3 flex items-center gap-1.5">
                    <ArrowUpCircle size={13} className="text-red-400" /> Withdrawal History
                  </h4>
                  <ul className="max-h-48 overflow-y-auto space-y-1.5 pr-1">
                    {userDetails.withdrawals?.length > 0 ? userDetails.withdrawals.map(w => (
                      <li key={w._id} className="flex justify-between items-center bg-white/3 border border-white/5 rounded-lg px-3 py-1.5 text-xs gap-2">
                        <span className="font-mono text-red-400 font-semibold shrink-0">-${w.amount?.toFixed(2)}</span>
                        <WithdrawStatusPill status={w.status} />
                        <span className="text-gray-600 shrink-0">{new Date(w.createdAt).toLocaleDateString()}</span>
                      </li>
                    )) : <li className="text-gray-600 text-xs">No withdrawals</li>}
                  </ul>
                </div>

                {/* Totals */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 rounded-xl bg-green-500/8 border border-green-500/15 text-center">
                    <p className="text-[10px] text-gray-500 mb-1">Total Deposited</p>
                    <p className="text-lg font-bold text-green-400">${userDetails.totalDepositAmount?.toFixed(2) || '0.00'}</p>
                  </div>
                  <div className="p-3 rounded-xl bg-red-500/8 border border-red-500/15 text-center">
                    <p className="text-[10px] text-gray-500 mb-1">Total Withdrawn</p>
                    <p className="text-lg font-bold text-red-400">${userDetails.totalWithdrawAmount?.toFixed(2) || '0.00'}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-10 text-red-400 text-sm">Failed to load user details.</div>
        )}
      </div>
    </div>
  </div>
);

export default UserDetailsModal;
